import { useCallback, useMemo } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000ComboBox from "../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000CheckBoxBase from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import U2000TagAutocomplete from "../../../../u2000Core/components/tables/commons/search/U2000TagAutocomplete";
import type { U2000CriteriaTag } from "../../../../u2000Core/components/tables/commons/search/u2000CriteriaTags";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU3139FindEmployeeHandlers } from "../../../U3139UseFindEmployeeHandlers";
import { NS } from "../../../U3139";
import {
  u3139CollectSecondaryWorkNatureIds,
  u3139FormatFamilyLabel,
  u3139FormatLegacyWorkNatureDisplay,
  u3139FormatWorkNatureOptionLabel,
  u3139MergeWorkNatureCatalogWithSelection,
  u3139ResolveWorkNatureId,
  type U3139MinutesFormRow,
  type U3139SecondaryWorkNatureLine,
  type U3139WorkNatureItem,
} from "../../../U3139Service";
import {
  sortU3139WorkNaturesByLabel,
  u3139FilterWorkNaturesForSecondaryAutocomplete,
} from "../../list/U3139WorkNatureCriteria";

interface U3139MinutesBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3139MinutesFormRow>;
  t: TFunction;
  workNatures: U3139WorkNatureItem[];
  projectLocked?: boolean;
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <U2000Label label={label} />
      <div className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500">{value || "\u00A0"}</div>
    </div>
  );
}

function U3139MinutesBody({ formStateHandlers, t, workNatures, projectLocked: _projectLocked = false }: U3139MinutesBodyProps) {
  const { i18n } = useTranslation();
  const findEmployeeHandlers = useU3139FindEmployeeHandlers();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const isEditMode = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
  const data = formStateHandlers.getData();

  const workNatureCatalogForCombo = useMemo(
    () => u3139MergeWorkNatureCatalogWithSelection(workNatures, data?.workNature),
    [workNatures, data?.workNature],
  );

  const workNatureOptions = useMemo(
    () =>
      sortU3139WorkNaturesByLabel(workNatureCatalogForCombo, i18n.language).map((wn) => ({
        ...wn,
        label: u3139FormatWorkNatureOptionLabel(wn, i18n.language, workNatureCatalogForCombo),
      })),
    [workNatureCatalogForCombo, i18n.language],
  );

  const findWorkNatureByValue = useCallback(
    (value: unknown, options: (U3139WorkNatureItem & { label?: string })[]) => {
      if (value == null || value === "") return null;
      const id = u3139ResolveWorkNatureId(value as U3139WorkNatureItem | string);
      if (!id) return null;
      return options.find((item) => String(item.id) === id) ?? null;
    },
    [],
  );

  const workNatureComboKey = u3139ResolveWorkNatureId(data?.workNature) ?? "none";

  const primaryWorkNatureFamilyLabel = useMemo(() => {
    const wn = data?.workNature;
    if (!wn || typeof wn !== "object") return "";
    return u3139FormatFamilyLabel(wn.family, i18n.language);
  }, [data?.workNature, i18n.language]);

  const secondaryTags = useMemo((): U2000CriteriaTag[] => {
    return (data?.secondaryWorkNatures ?? []).map((line) => {
      const wn = line.workNature;
      const id = u3139ResolveWorkNatureId(wn) ?? "";
      return {
        value: id,
        label: u3139FormatWorkNatureOptionLabel(wn, i18n.language, workNatureCatalogForCombo),
        meta: wn,
      };
    });
  }, [data?.secondaryWorkNatures, i18n.language, workNatureCatalogForCombo]);

  const excludedSecondaryIds = useMemo(() => {
    const set = new Set<string>();
    const primaryId = u3139ResolveWorkNatureId(data?.workNature);
    if (primaryId) set.add(primaryId);
    u3139CollectSecondaryWorkNatureIds(data?.secondaryWorkNatures).forEach((id) => set.add(id));
    return set;
  }, [data?.workNature, data?.secondaryWorkNatures]);

  const onSecondaryTagsChange = useCallback(
    (tags: U2000CriteriaTag[]) => {
      if (!data) return;
      const nextLines: U3139SecondaryWorkNatureLine[] = tags
        .map((tag) => {
          const meta = tag.meta as U3139WorkNatureItem | undefined;
          const id = tag.value?.trim() || meta?.id?.trim();
          if (!id) return null;
          const fromCatalog = workNatures.find((w) => w.id === id);
          const workNature = fromCatalog ?? meta ?? { id };
          return { workNature };
        })
        .filter((line): line is U3139SecondaryWorkNatureLine => line != null);
      formStateHandlers.setData({ ...data, secondaryWorkNatures: nextLines });
    },
    [data, formStateHandlers, workNatures],
  );

  const onSearchEmployee = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findEmployeeHandlers.search(criterias),
    [findEmployeeHandlers],
  );

  const extractEmployee = useCallback((result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "surveyor.id", value: result.id },
        { fieldName: "surveyor.firstName", value: result.firstName },
        { fieldName: "surveyor.lastName", value: result.lastName },
        { fieldName: "surveyor.codeEmployee", value: result.codeEmployee },
      ];
    }
    return [
      { fieldName: "surveyor.id", value: null },
      { fieldName: "surveyor.firstName", value: null },
      { fieldName: "surveyor.lastName", value: null },
      { fieldName: "surveyor.codeEmployee", value: null },
    ];
  }, []);

  const displayEmployee = useCallback((item: Record<string, unknown>) => {
    const first = String(item.firstName ?? "").trim();
    const last = String(item.lastName ?? "").trim();
    const code = String(item.codeEmployee ?? "").trim();
    const name = [first, last].filter(Boolean).join(" ");
    return code ? `${name} (${code})` : name;
  }, []);

  const projectDisplay = data?.project
    ? `${data.project.projetNumber ?? ""}${data.project.projetName ? ` — ${data.project.projetName}` : ""}`.trim()
    : "";

  const hasLegacyWorkNature = Boolean(data?.legacyWorkNatureCode?.trim() || data?.legacyWorkNatureLabel?.trim());

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-4">
        <div className="min-w-0 shrink-0 md:w-36">
          <U2000InputText
            fieldName="minuteNumber"
            label={t("lblMinuteNumber", { ns: NS })}
            formStateHandlers={formStateHandlers}
            readonly={readonly || isEditMode}
          />
        </div>
        <div className="min-w-0 flex-1">
          <U2000Label label={t("lblSurveyor", { ns: NS })} />
          <U2000AutoCompleteInput
            fieldName="surveyor"
            mainFieldName="surveyor.codeEmployee"
            entityLink="U3101"
            formStateHandlers={formStateHandlers}
            onSearch={onSearchEmployee}
            overrideExtractDataFn={extractEmployee}
            displayFn={displayEmployee}
            readonly={readonly}
          />
        </div>
        <div className="min-w-0 shrink-0">
          <U2000InputCalendarCustom
            fieldName="minuteDate"
            label={t("lblMinuteDate", { ns: NS })}
            formStateHandlers={formStateHandlers}
            readonly={readonly}
            inlineEnd={
              <div className="flex flex-wrap items-center gap-6 sm:gap-8">
                <U2000CheckBoxBase
                  fieldName="isRenovation"
                  label={t("lblIsRenovation", { ns: NS })}
                  value={data?.isRenovation ?? false}
                  setValue={(v) => formStateHandlers.setData({ ...data, isRenovation: v })}
                  readonly={readonly}
                  className="whitespace-nowrap"
                />
                <U2000CheckBoxBase
                  fieldName="isInactive"
                  label={t("lblIsInactive", { ns: NS })}
                  value={data?.isInactive ?? false}
                  setValue={(v) => formStateHandlers.setData({ ...data, isInactive: v })}
                  readonly={readonly}
                  className="whitespace-nowrap"
                />
              </div>
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ReadonlyField label={t("lblProject", { ns: NS })} value={projectDisplay} />
        <U2000InputText fieldName="clientName" label={t("lblClientName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
        <U2000InputText fieldName="streetAddress" label={t("lblStreetAddress", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
        <div className="space-y-1">
          <U2000ComboBox<U3139WorkNatureItem & { label: string }>
            key={workNatureComboKey}
            fieldName="workNature"
            label={t("lblWorkNature", { ns: NS })}
            formStateHandlers={formStateHandlers}
            data={workNatureOptions}
            keyField="id"
            descField="label"
            getValue={(item) => item.id ?? ""}
            findItemByValue={findWorkNatureByValue as any}
            formatLabel={(item) => item.label ?? u3139FormatWorkNatureOptionLabel(item, i18n.language, workNatures)}
            readonly={readonly}
            required
          />
          {primaryWorkNatureFamilyLabel ? (
            <p className="text-xs text-gray-500">
              {t("lblWorkNatureFamily", { ns: NS })} : {primaryWorkNatureFamilyLabel}
            </p>
          ) : null}
        </div>
        <U2000InputText fieldName="planNumber" label={t("lblPlanNumber", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
        <U2000InputText fieldName="notebookNumber" label={t("lblNotebookNumber", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
        <U2000InputText fieldName="pageNumber" label={t("lblPageNumber", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <div className={`min-w-0 space-y-1.5 ${hasLegacyWorkNature ? "md:w-2/3" : "w-full"}`}>
          <U2000Label label={t("lblSecondaryWorkNatures", { ns: NS })} />
          <U2000InvalidFeedback formStateHandlers={formStateHandlers as any} fieldName="secondaryWorkNatures" />
          <U2000TagAutocomplete<U3139WorkNatureItem>
            value={secondaryTags}
            onChange={onSecondaryTagsChange}
            disabled={readonly}
            allowFreeText={false}
            minSearchLength={0}
            searchCriterionField="workNatureCode"
            placeholder={t("phSecondaryWorkNatures", { ns: NS })}
            searchingLabel={t("lblSearching", { ns: "common" })}
            noResultsLabel={(query) => t("lblNoSecondaryWorkNatureResults", { ns: NS, query })}
            onSearch={async (criterias) => {
              const query = String(criterias.find((c) => c.fieldName === "workNatureCode")?.value ?? "");
              return u3139FilterWorkNaturesForSecondaryAutocomplete(
                workNatures,
                excludedSecondaryIds,
                query,
                i18n.language,
                workNatureCatalogForCombo,
              );
            }}
            itemToTag={(wn) => ({
              value: wn.id ?? "",
              label: u3139FormatWorkNatureOptionLabel(wn, i18n.language, workNatures),
              meta: wn,
            })}
            getTagKey={(tag) => tag.value}
            getTagLabel={(tag) => tag.label ?? tag.value}
            removeTagAriaLabel={(tag) => t("ariaRemoveSecondaryWorkNature", { ns: NS, label: tag.label ?? tag.value })}
          />
        </div>
        {hasLegacyWorkNature ? (
          <div className="min-w-0 space-y-1 md:w-1/3">
            <h3 className="text-sm font-semibold text-gray-800">{t("lblLegacyWorkNatureSection", { ns: NS })}</h3>
            <ReadonlyField
              label=""
              value={u3139FormatLegacyWorkNatureDisplay(data?.legacyWorkNatureLabel, data?.legacyWorkNatureCode)}
            />
          </div>
        ) : null}
      </div>

      <U2000InputText fieldName="remark" label={t("lblRemark", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readonly} />
    </div>
  );
}

export default U3139MinutesBody;
